import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, Plane, Car, Heart, Mountain, Briefcase, Users,
  UserCog, MapPinned, ShieldCheck, Clock, Award, Star, MessageCircle,
} from "lucide-react";
import { BUSINESS, SERVICES, FLEET, DESTINATIONS, IMG } from "@/data/site";
import Gallery from "@/components/Gallery";
import Reviews from "@/components/Reviews";
import SuccessModal from "@/components/SuccessModal";
import Seo from "@/components/Seo";
import { submitBooking } from "@/lib/bookingApi";

const ICONS = { Plane, Car, Heart, Mountain, Briefcase, Users, UserCog, MapPinned };

const wa = (n, m) =>
  `https://wa.me/91${n}?text=${encodeURIComponent(m || "Hello Chakri Travels, I want to book a car rental service.")}`;

const Hero = () => (
  <section data-testid="hero-section" className="relative isolate flex min-h-[100svh] items-center overflow-hidden">
    <img
      src={IMG.hero}
      alt="Luxury car at sunset on Andhra Pradesh coastal road"
      width="1920"
      height="1080"
      className="absolute inset-0 -z-10 h-full w-full object-cover"
    />
    {/* Royal blue tint over the hero — keeps the car image visible but improves text contrast across the entire viewport */}
    <div className="absolute inset-0 -z-10 bg-[var(--royal-deep)]/40" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[var(--royal-deep)]/90 via-[var(--royal-deep)]/70 to-[var(--royal)]/55" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/65 via-transparent to-black/30" />

    <div className="mx-auto w-full max-w-7xl px-5 pt-32 pb-20 lg:px-8 lg:pt-40">
      <div className="max-w-3xl reveal">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/60 glass-dark px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--gold)]">
          <Sparkles size={14} /> {BUSINESS.hero_tagline}
        </div>
        <h1 className="font-display text-3xl font-bold leading-[1.1] text-white sm:text-5xl lg:text-7xl hero-text-shadow">
          Premium <span className="text-gold-on-image">Car Rental</span> & Travel Services in Andhra Pradesh
        </h1>
        <p className="mt-6 max-w-2xl text-base font-medium text-white sm:text-lg hero-text-shadow-soft">
          Luxury chauffeur-driven cars across{" "}
          <span className="font-semibold text-[var(--gold)]">
            Kakinada, Rajamahendravaram, Narasapuram & Amalapuram
          </span>
          . Travel in elegance with India's most trusted local fleet.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link
            to="/contact"
            data-testid="hero-book-btn"
            className="group inline-flex items-center gap-2 rounded-full gradient-gold px-7 py-4 text-base font-semibold text-[var(--royal-deep)] shadow-xl shadow-[var(--gold)]/40 transition-transform hover:-translate-y-0.5"
          >
            Book Now <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            data-testid="hero-wa-btn"
            href={wa(BUSINESS.primaryWhatsApp)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-base font-semibold text-white shadow-xl shadow-[#25D366]/30 hover:-translate-y-0.5 transition-transform"
          >
            <MessageCircle size={18} /> Chat on WhatsApp
          </a>
        </div>

        <div className="mt-12 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { n: "12+", l: "Years" },
            { n: "50+", l: "Luxury Cars" },
            { n: "10k+", l: "Happy Trips" },
            { n: "4.9★", l: "Rated" },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl glass-dark px-4 py-3 text-white">
              <div className="font-display text-2xl font-bold text-[var(--gold)] hero-text-shadow-soft">{s.n}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-[11px] font-semibold uppercase tracking-[0.3em] float-y hero-text-shadow-soft">
      Scroll
    </div>
  </section>
);

const Services = () => (
  <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
    <div className="mx-auto max-w-2xl text-center">
      <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
        <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> What we offer
      </div>
      <h2 className="font-display text-[1.85rem] leading-[1.1] font-bold text-[var(--royal-deep)] sm:text-5xl [text-wrap:balance]">
        Premium travel, <span className="text-gradient-gold whitespace-nowrap">tailored to you</span>
      </h2>
      <p className="mt-4 text-base text-muted-foreground">
        From airport pickups to grand wedding entries — Chakri Travels delivers a curated, luxury experience for every occasion across Andhra Pradesh.
      </p>
    </div>

    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {SERVICES.map((s, i) => {
        const Icon = ICONS[s.icon];
        return (
          <div
            key={s.title}
            data-testid={`service-card-${i}`}
            className="group relative overflow-hidden rounded-2xl border border-[var(--royal)]/10 bg-white p-6 shadow-[0_10px_30px_-15px_rgba(15,23,80,0.18)] transition-all hover:-translate-y-1 hover:border-[var(--gold)]/50 hover:shadow-[0_25px_60px_-20px_rgba(15,23,80,0.35)]"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[var(--gold)]/10 blur-2xl transition-opacity group-hover:bg-[var(--gold)]/30" />
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl gradient-royal text-white ring-1 ring-[var(--gold)]/50">
                <Icon size={22} />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-[var(--royal-deep)]">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              <Link to="/contact" data-testid={`home-service-enquire-${i}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--royal)] hover:text-[var(--gold)]">
                Enquire now <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

const Fleet = () => (
  <div className="relative overflow-hidden bg-[linear-gradient(180deg,white,#f4f6fb)]">
    <section data-testid="fleet-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
      <div className="grid items-end gap-6 md:grid-cols-2">
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> The fleet
          </div>
          <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-5xl">
            Drive in <span className="text-gradient-gold">absolute luxury</span>
          </h2>
        </div>
        <p className="text-base text-muted-foreground md:text-right">
          Every vehicle is meticulously maintained, sanitised and driven by a vetted chauffeur trained in five-star hospitality.
        </p>
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {FLEET.map((f, i) => (
          <div
            key={f.name}
            data-testid={`fleet-card-${i}`}
            className="group overflow-hidden rounded-3xl bg-white shadow-[0_15px_40px_-20px_rgba(15,23,80,0.3)] ring-1 ring-[var(--royal)]/10 transition-all hover:-translate-y-1 hover:ring-[var(--gold)]/50"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={f.img}
                alt={f.name}
                width="1024"
                height="768"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--royal-deep)]/60 via-transparent to-transparent" />
              <div className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-[var(--royal-deep)]">
                Premium
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-display text-xl font-semibold text-[var(--royal-deep)]">{f.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.sub}</p>
              <a
                data-testid={`fleet-reserve-${i}`}
                href={wa(BUSINESS.primaryWhatsApp, `Hi Chakri Travels, I want to book a ${f.name}.`)}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--royal)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              >
                Reserve <ArrowRight size={14} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);

const Destinations = () => (
  <section data-testid="destinations-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
    <div className="mx-auto max-w-2xl text-center">
      <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
        <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Popular destinations
      </div>
      <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-5xl">
        Explore the <span className="text-gradient-gold">soul of AP</span>
      </h2>
    </div>

    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {DESTINATIONS.map((d, i) => (
        <div key={d.name} data-testid={`dest-card-${i}`} className="group relative h-80 overflow-hidden rounded-3xl ring-1 ring-[var(--royal)]/10">
          <img
            src={d.img}
            alt={d.name}
            width="1024"
            height="768"
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--royal-deep)]/90 via-[var(--royal-deep)]/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--gold)]">{d.tag}</div>
            <div className="font-display text-xl font-semibold">{d.name}</div>
            <a
              href={wa(BUSINESS.primaryWhatsApp, `Hi, please share a tour plan for ${d.name}.`)}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-white/90 hover:text-[var(--gold)]"
            >
              Plan a trip <ArrowRight size={14} />
            </a>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const WhyUs = () => (
  <div className="relative overflow-hidden gradient-royal text-white">
    <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-[var(--gold)]/20 blur-3xl" />
    <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
    <section data-testid="why-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-soft)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Why Chakri Travels
          </div>
          <h2 className="font-display text-3xl font-bold text-white sm:text-5xl">
            Luxury you can feel. <br />
            <span className="text-gradient-gold">Service you can trust.</span>
          </h2>
          <p className="mt-5 max-w-lg text-white/80">
            For over a decade, families, executives and event planners across Andhra Pradesh have trusted Chakri Travels for moments that matter — because every journey deserves to feel premium.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            { I: ShieldCheck, t: "Trusted & Safe", d: "Verified chauffeurs, GPS-tracked, fully insured fleet." },
            { I: Clock, t: "Always On Time", d: "We track flights and traffic so you never wait." },
            { I: Award, t: "Award-Winning Service", d: "AP's highest-rated premium travel brand." },
            { I: Star, t: "Transparent Pricing", d: "No hidden charges. Clear, fair, all-inclusive rates." },
          ].map((x, i) => (
            <div key={x.t} data-testid={`why-card-${i}`} className="rounded-2xl glass p-5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-gold text-[var(--royal-deep)]">
                <x.I size={20} />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-white">{x.t}</h3>
              <p className="mt-1 text-sm text-white/75">{x.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

const QuickEnquiry = () => {
  const [form, setForm] = React.useState({ name: "", mobile: "", pickup: "", destination: "", date: "" });
  const [busy, setBusy] = React.useState(false);
  const [popup, setPopup] = React.useState({ open: false, mode: "api", record: null });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const result = await submitBooking({
      customerName: form.name,
      customerPhone: form.mobile,
      pickupLocation: form.pickup,
      dropLocation: form.destination || form.pickup,
      pickupDate: form.date,
      pickupTime: "09:00",
      carType: "Premium SUV",
      passengers: 2,
      specialRequest: "",
    });
    setBusy(false);
    setPopup({ open: true, mode: result.mode, record: result.record });
    if (result.mode === "api") setForm({ name: "", mobile: "", pickup: "", destination: "", date: "" });
  };

  return (
    <>
      <section data-testid="enquiry-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="overflow-hidden rounded-3xl border border-[var(--gold)]/30 bg-white p-6 shadow-[0_25px_70px_-25px_rgba(15,23,80,0.35)] sm:p-10">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Quick enquiry
              </div>
              <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-4xl">
                Reserve in under a <span className="text-gradient-gold">minute</span>
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Tell us where and when — our team will reach out within minutes to confirm your booking.
              </p>
            </div>
            <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="enquiry-form" noValidate>
              <input data-testid="enq-name" required placeholder="Full Name" value={form.name} onChange={set("name")} className="form-input" />
              <input data-testid="enq-mobile" required placeholder="Mobile Number" type="tel" value={form.mobile} onChange={set("mobile")} className="form-input" />
              <input data-testid="enq-pickup" required placeholder="Pickup Location" value={form.pickup} onChange={set("pickup")} className="form-input" />
              <input data-testid="enq-destination" placeholder="Destination" value={form.destination} onChange={set("destination")} className="form-input" />
              <input data-testid="enq-date" required type="date" value={form.date} onChange={set("date")} className="form-input sm:col-span-2" />
              <button data-testid="enq-submit" type="submit" disabled={busy} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-xl gradient-royal px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[var(--royal)]/30 transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {busy ? "Sending…" : "Get Instant Quote"}
              </button>
            </form>
          </div>
        </div>
      </section>
      <SuccessModal open={popup.open} mode={popup.mode} booking={popup.record} onClose={() => setPopup({ ...popup, open: false })} />
    </>
  );
};

const FinalCTA = () => (
  <section data-testid="final-cta-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
    <div className="relative overflow-hidden rounded-3xl gradient-royal p-10 text-white sm:p-16">
      <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[var(--gold)]/30 blur-3xl" />
      <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        <div>
          <h2 className="max-w-2xl font-display text-3xl font-bold sm:text-5xl">
            Ready for a <span className="text-gradient-gold">luxury ride</span>?
          </h2>
          <p className="mt-3 max-w-xl text-white/80">
            Speak with our team and travel like royalty across Andhra Pradesh today.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a data-testid="cta-call-1" href={`tel:+91${BUSINESS.phones[0]}`} className="rounded-full bg-white px-7 py-4 text-sm font-semibold text-[var(--royal-deep)]">
            Call +91 {BUSINESS.phones[0]}
          </a>
          <a data-testid="cta-call-2" href={`tel:+91${BUSINESS.phones[1]}`} className="rounded-full bg-white/15 ring-1 ring-white/30 px-7 py-4 text-sm font-semibold text-white">
            Call +91 {BUSINESS.phones[1]}
          </a>
          <Link to="/contact" data-testid="cta-book" className="rounded-full gradient-gold px-7 py-4 text-sm font-semibold text-[var(--royal-deep)]">
            Book Now
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  React.useEffect(() => {
    document.title = `Chakri Travels — Luxury Car Rental in Andhra Pradesh | ${BUSINESS.domain}`;
  }, []);
  return (
    <main>
      <Seo
        title="Chakri Travels — Luxury Car Rental in Kakinada, Andhra Pradesh"
        description="Premium chauffeur-driven cars in Kakinada, Rajamahendravaram, Narasapuram & Amalapuram. Airport transfers, weddings, outstation & AP tour packages. Call +91 90591 14439."
        path="/"
      />
      <Hero />
      <Services />
      <Fleet />
      <Gallery />
      <Destinations />
      <WhyUs />
      <Reviews />
      <QuickEnquiry />
      <FinalCTA />
    </main>
  );
};

export default Home;
